import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { 
  Download, Trash2, Shield, FileText, AlertTriangle, 
  CheckCircle, Loader2, ArrowLeft 
} from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../components/ui/alert-dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const PrivacySettings = () => {
  const { t } = useLanguage();
  const { user, token, logout } = useAuth();
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [consentStatus, setConsentStatus] = useState({
    gdpr_consent: false,
    marketing_consent: false
  });

  useEffect(() => {
    fetchConsentStatus();
  }, []);

  const fetchConsentStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/gdpr/consent-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConsentStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch consent status:', error);
    }
  };

  const updateConsent = async (field, value) => {
    setLoading(true);
    try {
      const newStatus = { ...consentStatus, [field]: value };
      await axios.post(`${API_URL}/api/gdpr/update-consent`, null, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          gdpr_consent: newStatus.gdpr_consent,
          marketing_consent: newStatus.marketing_consent
        }
      });
      setConsentStatus(newStatus);
      toast.success('Préférences mises à jour');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setLoading(false);
    }
  };

  const exportData = async () => {
    setExportLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/gdpr/export`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Create and download JSON file
      const dataStr = JSON.stringify(response.data.data, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `drbooking_export_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success('Vos données ont été exportées');
    } catch (error) {
      toast.error('Erreur lors de l\'export');
    } finally {
      setExportLoading(false);
    }
  };

  const deleteAccount = async () => {
    setDeleteLoading(true);
    try {
      await axios.post(`${API_URL}/api/gdpr/delete-account`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Votre compte a été supprimé');
      setTimeout(() => logout(), 2000);
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 py-8 px-4" data-testid="privacy-settings-page">
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="flex items-center gap-4 mb-6">
          <Link to="/dashboard" className="text-slate-500 hover:text-slate-700 dark:text-slate-400">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
              Confidentialité & Données
            </h1>
            <p className="text-slate-600 dark:text-slate-400">
              Gérez vos préférences de confidentialité et vos données personnelles
            </p>
          </div>
        </div>

        {/* Consent Management */}
        <Card data-testid="consent-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-blue-600" />
              Consentements
            </CardTitle>
            <CardDescription>
              Gérez vos autorisations de traitement de données
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
              <div className="space-y-1">
                <Label className="font-medium">Consentement RGPD</Label>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Autorisation de traitement des données personnelles pour les services drbooking
                </p>
              </div>
              <div className="flex items-center gap-2">
                {consentStatus.gdpr_consent ? (
                  <CheckCircle className="w-4 h-4 text-green-500" />
                ) : (
                  <AlertTriangle className="w-4 h-4 text-amber-500" />
                )}
                <Switch
                  checked={consentStatus.gdpr_consent}
                  onCheckedChange={(checked) => updateConsent('gdpr_consent', checked)}
                  disabled={loading}
                  data-testid="gdpr-consent-switch"
                />
              </div>
            </div>
            
            <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
              <div className="space-y-1">
                <Label className="font-medium">Communications marketing</Label>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Recevoir des emails promotionnels et des actualités drbooking
                </p>
              </div>
              <Switch
                checked={consentStatus.marketing_consent}
                onCheckedChange={(checked) => updateConsent('marketing_consent', checked)}
                disabled={loading}
                data-testid="marketing-consent-switch"
              />
            </div>
          </CardContent>
        </Card>

        {/* Data Export */}
        <Card data-testid="export-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Download className="w-5 h-5 text-green-600" />
              Export de données
            </CardTitle>
            <CardDescription>
              Téléchargez une copie de toutes vos données personnelles (RGPD Art. 20)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
              <p className="text-sm text-green-800 dark:text-green-200 mb-4">
                Cet export inclut : vos informations personnelles, l'historique de vos rendez-vous, 
                vos paiements, vos préférences et vos favoris.
              </p>
              <Button 
                onClick={exportData} 
                disabled={exportLoading}
                className="bg-green-600 hover:bg-green-700"
                data-testid="export-data-btn"
              >
                {exportLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Export en cours...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Télécharger mes données
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Account Deletion */}
        <Card className="border-red-200 dark:border-red-800" data-testid="delete-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Suppression du compte
            </CardTitle>
            <CardDescription>
              Supprimer définitivement votre compte et toutes vos données (RGPD Art. 17)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-800 dark:text-red-200 mb-4">
                <strong>Attention :</strong> Cette action est irréversible. Toutes vos données seront 
                définitivement supprimées, y compris votre historique de rendez-vous.
              </p>
              
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button 
                    variant="destructive"
                    data-testid="delete-account-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Supprimer mon compte
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle className="flex items-center gap-2 text-red-600">
                      <AlertTriangle className="w-5 h-5" />
                      Confirmer la suppression
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      Êtes-vous sûr de vouloir supprimer définitivement votre compte ?
                      <br /><br />
                      Cette action supprimera :
                      <ul className="list-disc list-inside mt-2 space-y-1">
                        <li>Vos informations personnelles</li>
                        <li>Votre historique de rendez-vous</li>
                        <li>Vos paiements et factures</li>
                        <li>Vos favoris et préférences</li>
                      </ul>
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Annuler</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={deleteAccount}
                      className="bg-red-600 hover:bg-red-700"
                      disabled={deleteLoading}
                      data-testid="confirm-delete-btn"
                    >
                      {deleteLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Suppression...
                        </>
                      ) : (
                        'Oui, supprimer mon compte'
                      )}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </CardContent>
        </Card>

        {/* Legal Links */}
        <Card data-testid="legal-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-slate-600" />
              Documents légaux
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-4">
            <Link 
              to="/privacy" 
              className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
            >
              <h3 className="font-medium text-slate-900 dark:text-white">Politique de confidentialité</h3>
              <p className="text-sm text-slate-500 dark:text-slate-400">Comment nous utilisons vos données</p>
            </Link>
            <Link 
              to="/terms" 
              className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
            >
              <h3 className="font-medium text-slate-900 dark:text-white">CGU</h3>
              <p className="text-sm text-slate-500 dark:text-slate-400">Conditions d'utilisation</p>
            </Link>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PrivacySettings;
